import type { ApiClient } from '@momonos/api-client'
import { formatBdt, SEGMENT_NAMES } from '@momonos/shared'
import { Badge, Button, Card, Loader, useToast } from '@momonos/ui'
import { useCallback, useEffect, useState } from 'react'

/**
 * FR-MKT — the campaign builder.
 *
 * The flow the API enforces is visible in the buttons: a draft can only be
 * approved, an approved one only dispatched or killed, and the preview count
 * sits beside the audience so nobody sends blind.
 */

interface CampaignRow {
  id: string
  name: string
  status: 'draft' | 'approved' | 'sending' | 'sent' | 'killed'
  channel: string
  trigger: string
  audienceJson: string
  titleEn: string
  abSplitBp: number
}

interface Stats {
  sends: Array<{ status: string; variant: string; count: number; clicks: number }>
  redeemedOrders: number
  revenue: number
}

const STATUS_TONES: Record<CampaignRow['status'], 'neutral' | 'green' | 'yellow' | 'red'> = {
  draft: 'neutral',
  approved: 'yellow',
  sending: 'yellow',
  sent: 'green',
  killed: 'red',
}

export function CampaignsScreen({ api }: { api: ApiClient; branchId: string | null }) {
  const { show } = useToast()
  const [rows, setRows] = useState<CampaignRow[] | null>(null)
  const [creating, setCreating] = useState(false)
  const [stats, setStats] = useState<Record<string, Stats>>({})

  const reload = useCallback(async () => {
    try {
      const result = await api.get<{ campaigns: CampaignRow[] }>('/v1/admin/campaigns')
      setRows(result.campaigns)
    } catch {
      show('Could not load campaigns', { tone: 'error' })
    }
  }, [api, show])

  useEffect(() => {
    void reload()
  }, [reload])

  async function act(id: string, action: 'approve' | 'dispatch' | 'kill') {
    try {
      const result = await api.post<{ sent?: number; skippedNoConsent?: number }>(
        `/v1/admin/campaigns/${id}/${action}`,
        {},
      )
      if (action === 'dispatch') {
        show(`Sent ${result.sent ?? 0}, skipped ${result.skippedNoConsent ?? 0} (no consent)`)
      }
      await reload()
    } catch (caught) {
      show((caught as Error).message ?? `Could not ${action}`, { tone: 'error' })
    }
  }

  async function loadStats(id: string) {
    try {
      const result = await api.get<Stats>(`/v1/admin/campaigns/${id}/stats`)
      setStats((current) => ({ ...current, [id]: result }))
    } catch {
      show('Could not load stats', { tone: 'error' })
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-bold text-2xl text-content">Campaigns</h1>
        <Button onClick={() => setCreating((current) => !current)}>
          {creating ? 'Close' : 'New campaign'}
        </Button>
      </div>

      {creating && (
        <NewCampaignForm
          api={api}
          onCreated={async () => {
            setCreating(false)
            await reload()
          }}
        />
      )}

      {!rows ? (
        <Loader label="Loading campaigns" />
      ) : rows.length === 0 ? (
        <p className="text-content-muted">No campaigns yet.</p>
      ) : (
        <div className="grid gap-3">
          {rows.map((campaign) => (
            <Card key={campaign.id} className="space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <p className="font-semibold text-content">{campaign.name}</p>
                <Badge tone={STATUS_TONES[campaign.status]}>{campaign.status}</Badge>
                <Badge tone="neutral">{campaign.channel}</Badge>
                {campaign.trigger !== 'manual' && <Badge tone="neutral">{campaign.trigger}</Badge>}
                {campaign.abSplitBp > 0 && <Badge tone="yellow">A/B</Badge>}

                <span className="ml-auto flex gap-2">
                  {campaign.status === 'draft' && (
                    <Button size="sm" onClick={() => act(campaign.id, 'approve')}>
                      Approve
                    </Button>
                  )}
                  {campaign.status === 'approved' && campaign.trigger === 'manual' && (
                    <Button size="sm" onClick={() => act(campaign.id, 'dispatch')}>
                      Send now
                    </Button>
                  )}
                  {(campaign.status === 'approved' || campaign.status === 'sending') && (
                    <Button size="sm" variant="danger" onClick={() => act(campaign.id, 'kill')}>
                      Kill
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => loadStats(campaign.id)}>
                    Stats
                  </Button>
                </span>
              </div>

              <p className="text-content-muted text-sm">
                “{campaign.titleEn}” · audience {campaign.audienceJson}
              </p>

              {stats[campaign.id] && (
                <div className="flex flex-wrap gap-4 rounded-lg bg-surface-overlay p-3 text-sm">
                  {stats[campaign.id]?.sends.map((row) => (
                    <span key={`${row.status}-${row.variant}`} className="text-content-secondary">
                      {row.status}
                      {campaign.abSplitBp > 0 ? ` (${row.variant.toUpperCase()})` : ''}: {row.count}
                      {row.clicks > 0 ? ` · ${row.clicks} clicks` : ''}
                    </span>
                  ))}
                  <span className="text-content">
                    {stats[campaign.id]?.redeemedOrders} redeemed ·{' '}
                    {formatBdt(stats[campaign.id]?.revenue ?? 0)} revenue
                  </span>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

function NewCampaignForm({ api, onCreated }: { api: ApiClient; onCreated: () => Promise<void> }) {
  const { show } = useToast()
  const [busy, setBusy] = useState(false)
  const [previewCount, setPreviewCount] = useState<number | null>(null)
  const [form, setForm] = useState({
    name: '',
    channel: 'banner',
    trigger: 'manual',
    inactiveDays: '',
    minSpentBdt: '',
    minOrders: '',
    segment: '',
    birthdayToday: false,
    titleEn: '',
    titleBn: '',
    bodyEn: '',
    couponType: '',
    couponValue: '',
    grantPoints: '',
  })

  const set = (key: string) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }))

  function audience() {
    return {
      ...(form.inactiveDays ? { inactiveDays: Number(form.inactiveDays) } : {}),
      ...(form.minSpentBdt ? { minSpentBdt: Number(form.minSpentBdt) } : {}),
      ...(form.minOrders ? { minOrders: Number(form.minOrders) } : {}),
      ...(form.segment ? { segment: form.segment } : {}),
      ...(form.birthdayToday ? { birthdayToday: true } : {}),
    }
  }

  async function preview() {
    try {
      const result = await api.post<{ count: number }>(
        '/v1/admin/campaigns/preview-audience',
        audience(),
      )
      setPreviewCount(result.count)
    } catch {
      show('Add at least one audience condition', { tone: 'error' })
    }
  }

  async function submit() {
    setBusy(true)
    try {
      await api.post('/v1/admin/campaigns', {
        name: form.name,
        channel: form.channel,
        trigger: form.trigger,
        audience: audience(),
        titleEn: form.titleEn,
        titleBn: form.titleBn || form.titleEn,
        bodyEn: form.bodyEn || null,
        couponType: form.couponType || null,
        couponValue: form.couponValue ? Number(form.couponValue) : null,
        grantPoints: form.grantPoints ? Number(form.grantPoints) : null,
      })
      show('Draft created — approve it to make it sendable')
      await onCreated()
    } catch (caught) {
      show((caught as Error).message ?? 'Could not create', { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const input =
    'h-11 w-full rounded-lg border border-surface-border bg-surface-raised px-3 text-content text-sm'

  return (
    <Card className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <input
          className={input}
          placeholder="Campaign name"
          value={form.name}
          onChange={set('name')}
        />
        <select className={input} value={form.channel} onChange={set('channel')}>
          <option value="banner">In-app banner</option>
          <option value="sms">SMS</option>
          <option value="whatsapp">WhatsApp</option>
        </select>
        <select className={input} value={form.trigger} onChange={set('trigger')}>
          <option value="manual">Send manually</option>
          <option value="birthday">Birthdays (daily)</option>
          <option value="occasion">On an occasion date</option>
        </select>
      </div>

      <p className="font-medium text-content-secondary text-sm">
        Audience (conditions AND together)
      </p>
      <div className="grid gap-3 sm:grid-cols-4">
        <input
          className={input}
          placeholder="Inactive ≥ days"
          inputMode="numeric"
          value={form.inactiveDays}
          onChange={set('inactiveDays')}
        />
        <input
          className={input}
          placeholder="Spent ≥ ৳"
          inputMode="numeric"
          value={form.minSpentBdt}
          onChange={set('minSpentBdt')}
        />
        <input
          className={input}
          placeholder="Orders ≥"
          inputMode="numeric"
          value={form.minOrders}
          onChange={set('minOrders')}
        />
        <select className={input} value={form.segment} onChange={set('segment')}>
          <option value="">Any segment</option>
          {SEGMENT_NAMES.map((name) => (
            <option key={name} value={name}>
              {name.replace('_', ' ')}
            </option>
          ))}
        </select>
      </div>
      <label className="flex items-center gap-2 text-content-secondary text-sm">
        <input
          type="checkbox"
          checked={form.birthdayToday}
          onChange={(event) =>
            setForm((current) => ({ ...current, birthdayToday: event.target.checked }))
          }
        />
        Birthday is today
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <input
          className={input}
          placeholder="Title (English)"
          value={form.titleEn}
          onChange={set('titleEn')}
        />
        <input
          className={input}
          placeholder="শিরোনাম (বাংলা)"
          value={form.titleBn}
          onChange={set('titleBn')}
        />
      </div>
      <input
        className={input}
        placeholder="Body (optional)"
        value={form.bodyEn}
        onChange={set('bodyEn')}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <select className={input} value={form.couponType} onChange={set('couponType')}>
          <option value="">No coupon</option>
          <option value="percent">Percent off</option>
          <option value="fixed">৳ off</option>
        </select>
        <input
          className={input}
          placeholder={form.couponType === 'percent' ? 'Value in bp (1500 = 15%)' : 'Value ৳'}
          inputMode="numeric"
          value={form.couponValue}
          onChange={set('couponValue')}
        />
        <input
          className={input}
          placeholder="Grant points (optional)"
          inputMode="numeric"
          value={form.grantPoints}
          onChange={set('grantPoints')}
        />
      </div>

      <div className="flex items-center gap-3">
        <Button variant="secondary" onClick={preview}>
          Preview audience
        </Button>
        {previewCount !== null && (
          <span className="text-content-secondary text-sm">{previewCount} customers match</span>
        )}
        <Button
          isLoading={busy}
          disabled={!form.name || !form.titleEn}
          onClick={submit}
          className="ml-auto"
        >
          Create draft
        </Button>
      </div>
    </Card>
  )
}
